// @ts-nocheck
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { FileSpreadsheet, Loader2, Check, AlertCircle, Copy, Settings2, Download } from 'lucide-react';
import { EventFormData } from '@/components/admin-dashboard/CreateEventForm';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface IntegrationsTabProps {
  formData: EventFormData;
  updateFormData: (data: Partial<EventFormData>) => void;
  eventId?: string;
}

interface SheetTab {
  id: number;
  title: string;
  index: number;
}

interface SheetColumn {
  letter: string;
  name: string;
  index: number;
}

// Default column mappings for registrations
const REGISTRATION_COLUMNS = [
  { key: 'team_name', label: 'Team Name' },
  { key: 'division', label: 'Division' },
  { key: 'coach_first', label: 'Coach First Name' },
  { key: 'coach_last', label: 'Coach Last Name' },
  { key: 'email', label: 'Coach Email' },
  { key: 'cell', label: 'Phone' },
  { key: 'registrant_email', label: 'Registrant Email' },
  { key: 'registered_at', label: 'Registered Date' },
  { key: 'paid', label: 'Paid Status' },
  { key: 'pay_type', label: 'Payment Type' },
  { key: 'total_amount', label: 'Total Amount' },
  { key: 'amount_paid', label: 'Amount Paid' },
  { key: 'owe_still', label: 'Amount Owed' },
  { key: 'registration_amount', label: 'Registration Fee' },
  { key: 'extras_amount', label: 'Extras Amount' },
  { key: 'special_request_amount', label: 'Special Request Amount' },
  { key: 'tickets_amount', label: 'Tickets Amount' },
  { key: 'schedule_request', label: 'Schedule Request' },
  { key: 'rate_your_team', label: 'Rate Your Team' },
  { key: 'extras', label: 'Extras' },
];

// Summary row fields for totals
const SUMMARY_COLUMNS = [
  { key: 'total_teams', label: 'Total Teams Registered' },
  { key: 'total_paid', label: 'Total Paid Teams' },
  { key: 'total_pending', label: 'Total Pending Teams' },
];

// Default column mappings for media credentials
const MEDIA_CREDENTIALS_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'company', label: 'Company/Outlet' },
  { key: 'instagram_handle', label: 'Instagram' },
  { key: 'twitter_handle', label: 'X/Twitter' },
  { key: 'coverage_type', label: 'Coverage Type' },
  { key: 'submitted_at', label: 'Submitted Date' },
  { key: 'event_name', label: 'Event Name' },
  { key: 'status', label: 'Status' },
];

// Default column mappings for coach signups
const COACH_SIGNUP_COLUMNS = [
  { key: 'filler_name', label: 'Registrant Name' },
  { key: 'filler_email', label: 'Registrant Email' },
  { key: 'coach_name', label: 'Coach Name' },
  { key: 'email', label: 'Coach Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'college_name', label: 'College/University' },
  { key: 'division', label: 'Division' },
  { key: 'additional_coaches', label: 'Additional Coaches' },
  { key: 'additional_coach_count', label: 'Additional Coach Count' },
  { key: 'hospitality_days', label: 'Hospitality Days' },
  { key: 'payment_status', label: 'Payment Status' },
  { key: 'payment_amount', label: 'Payment Amount' },
  { key: 'registered_at', label: 'Registered Date' },
];

export default function IntegrationsTab({ formData, updateFormData, eventId }: IntegrationsTabProps) {
  const [testingConnection, setTestingConnection] = useState(false);
  const [testingMediaConnection, setTestingMediaConnection] = useState(false);
  const [testingCoachConnection, setTestingCoachConnection] = useState(false);
  const [sendingTestRow, setSendingTestRow] = useState<'registration' | 'media' | 'coach' | null>(null);
  const [downloadingHeaders, setDownloadingHeaders] = useState(false);
  const [connectionResult, setConnectionResult] = useState<{ 
    success: boolean; 
    message: string; 
    email?: string;
    tabs?: SheetTab[];
  } | null>(null);
  const [mediaConnectionResult, setMediaConnectionResult] = useState<{ 
    success: boolean; 
    message: string; 
    email?: string;
    tabs?: SheetTab[];
  } | null>(null);
  const [coachConnectionResult, setCoachConnectionResult] = useState<{ 
    success: boolean; 
    message: string; 
    email?: string;
    tabs?: SheetTab[];
  } | null>(null);
  const [showMappingDialog, setShowMappingDialog] = useState(false);
  const [showMediaMappingDialog, setShowMediaMappingDialog] = useState(false);
  const [showCoachMappingDialog, setShowCoachMappingDialog] = useState(false);
  const [sheetColumns, setSheetColumns] = useState<SheetColumn[]>([]);
  const [loadingColumns, setLoadingColumns] = useState(false);
  const { toast } = useToast();

  // Download template CSV with headers AND formulas
  const handleDownloadTemplate = async () => {
    if (!eventId) {
      toast({
        title: 'Error',
        description: 'Event must be saved first before downloading template.',
        variant: 'destructive'
      });
      return;
    }

    setDownloadingHeaders(true);
    try {
      // Fetch event pricing to get divisions
      const { data: pricing } = await supabase
        .from('event_pricing')
        .select('pricing_structure')
        .eq('event_id', eventId)
        .single();

      const pricingStructure = pricing?.pricing_structure as any;
      const divisions = pricingStructure?.division_pricing || [];

      // Registration field headers (these will have data starting row 3)
      const registrationHeaders = [
        'Team Name', 'Division', 'Coach First', 'Coach Last', 'Email', 'Phone',
        'Registered Date', 'Paid Status', 'Payment Type', 'Amount Paid', 'Amount Owed',
        'Schedule Request', 'Rate Your Team', 'Extras'
      ];

      // Summary headers (columns A onwards)
      const summaryHeaders = ['Total Teams', 'Total Paid', 'Total Pending'];
      const divisionHeaders = divisions.map((d: any) => `${d.name || d.division_name} Count`);
      
      // Calculate column letters
      const summaryCount = summaryHeaders.length + divisionHeaders.length;
      const spacerCol = summaryCount; // 0-indexed, this is the blank column
      const dataStartCol = spacerCol + 1; // Registration data starts here
      
      // Helper to get column letter from 0-indexed position
      const getColLetter = (index: number) => {
        let letter = '';
        let i = index;
        while (i >= 0) {
          letter = String.fromCharCode(65 + (i % 26)) + letter;
          i = Math.floor(i / 26) - 1;
        }
        return letter;
      };

      // Find column letters for formula references
      const teamNameCol = getColLetter(dataStartCol); // Team Name column
      const divisionCol = getColLetter(dataStartCol + 1); // Division column
      const paidStatusCol = getColLetter(dataStartCol + 7); // Paid column (8th registration field)

      // Build Row 1: Headers
      const row1Headers = [...summaryHeaders, ...divisionHeaders, '', ...registrationHeaders];

      // Build Row 2: Formulas
      // Total Teams = count of non-empty Team Name cells minus 2 (headers + formula row)
      // Since data starts row 3, we count from row 3 onwards
      // Use CHAR(34) to ensure proper quotes in Google Sheets formulas
      // This avoids any quote encoding issues when CSV is imported
      const q = String.fromCharCode(34); // ASCII double quote
      
      const formulaTotalTeams = `=COUNTA(${teamNameCol}3:${teamNameCol})`;
      const formulaTotalPaid = `=COUNTIF(${paidStatusCol}3:${paidStatusCol},${q}paid${q})`;
      const formulaTotalPending = `=COUNTIF(${paidStatusCol}3:${paidStatusCol},${q}pending${q})+COUNTIF(${paidStatusCol}3:${paidStatusCol},${q}pay_later${q})`;
      
      // Division count formulas
      const divisionFormulas = divisions.map((d: any) => {
        const divName = d.name || d.division_name || '';
        return `=COUNTIF(${divisionCol}3:${divisionCol},${q}${divName}${q})`;
      });

      const row2Formulas = [formulaTotalTeams, formulaTotalPaid, formulaTotalPending, ...divisionFormulas, '', ...registrationHeaders.map(() => '')];

      // Create CSV content with both rows
      // Escape any commas in headers/formulas using explicit ASCII quotes
      const dq = String.fromCharCode(34); // ASCII double quote "
      const escapeCSV = (val: string) => {
        if (val.includes(',') || val.includes(dq) || val.includes('\n')) {
          return dq + val.replace(new RegExp(dq, 'g'), dq + dq) + dq;
        }
        return val;
      };

      const csvRow1 = row1Headers.map(escapeCSV).join(',');
      const csvRow2 = row2Formulas.map(escapeCSV).join(',');
      const csvContent = csvRow1 + '\n' + csvRow2;
      
      // Download as UTF-8 CSV without BOM
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `google-sheets-template-${eventId.slice(0, 8)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: 'Template Downloaded!',
        description: `Row 1: Headers | Row 2: Formulas | Data starts Row 3. ${divisions.length} division counts included.`,
      });
    } catch (error: any) {
      console.error('Download template error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to download template',
        variant: 'destructive'
      });
    } finally {
      setDownloadingHeaders(false);
    }
  };

  const fetchSheetHeaders = async (spreadsheetId: string, tabName: string) => {
    setLoadingColumns(true);
    try {
      const { data, error } = await invokeEdgeFunction('google-sheets-sync', {
        body: {
          spreadsheetId,
          sheetName: tabName || 'Sheet1',
          action: 'get_headers'
        }
      });

      if (error) throw error;

      if (data?.success && data?.columns) {
        setSheetColumns(data.columns);
      } else {
        setSheetColumns([]);
        toast({
          title: 'No headers found',
          description: 'Make sure Row 1 has column headers in your sheet.',
          variant: 'destructive'
        });
      }
    } catch (error: any) {
      console.error('Error fetching headers:', error);
      setSheetColumns([]);
      toast({
        title: 'Error',
        description: error.message || 'Failed to fetch sheet headers',
        variant: 'destructive'
      });
    } finally {
      setLoadingColumns(false);
    }
  };

  const handleOpenMappingDialog = async (type: 'registration' | 'media' | 'coach') => {
    const spreadsheetId = type === 'registration' 
      ? formData.google_sheets_webhook_url 
      : type === 'media'
      ? (formData as any).media_credentials_sheets_id
      : (formData as any).coach_attendance_google_sheet_url;
    
    const tabName = type === 'registration'
      ? (formData as any).google_sheets_tab_name
      : type === 'media'
      ? (formData as any).media_credentials_sheets_tab_name
      : (formData as any).coach_attendance_sheets_tab_name;

    if (spreadsheetId) {
      await fetchSheetHeaders(spreadsheetId, tabName);
    }

    if (type === 'registration') {
      setShowMappingDialog(true);
    } else if (type === 'media') {
      setShowMediaMappingDialog(true);
    } else {
      setShowCoachMappingDialog(true);
    }
  };

  const handleSendTestRow = async (type: 'registration' | 'media' | 'coach') => {
    console.log('[IntegrationsTab] handleSendTestRow called:', type);
    
    const spreadsheetId = type === 'registration' 
      ? formData.google_sheets_webhook_url 
      : type === 'media'
      ? (formData as any).media_credentials_sheets_id
      : (formData as any).coach_attendance_google_sheet_url;
    
    const sheetName = type === 'registration'
      ? (formData as any).google_sheets_tab_name
      : type === 'media'
      ? (formData as any).media_credentials_sheets_tab_name
      : (formData as any).coach_attendance_sheets_tab_name;

    console.log('[IntegrationsTab] Test row params:', { spreadsheetId, sheetName, type });

    if (!spreadsheetId) {
      console.error('[IntegrationsTab] No spreadsheet ID for test row');
      toast({
        title: 'Error',
        description: 'Spreadsheet ID is missing. Please enter it and test connection first.',
        variant: 'destructive'
      });
      return;
    }

    setSendingTestRow(type);

    try {
      let testData: Record<string, string> = {};
      let action = 'append';
      let columnMapping = null;

      if (type === 'registration') {
        columnMapping = (formData as any).google_sheets_column_mapping || null;
        testData = {
          team_name: '** TEST ROW **',
          division: 'Test Division',
          coach_first: 'Test',
          coach_last: 'Coach',
          email: 'test@example.com',
          cell: '555-123-4567',
          registered: 'Yes',
          paid: 'Test',
          pay_type: 'Test',
          total_amount: '0',
          owe_still: '0',
          schedule_request: 'Test request',
          rate_your_team: 'Test rating',
          extras: 'Extra 1, Extra 2'
        };
      } else if (type === 'media') {
        action = 'append_media_credentials';
        testData = {
          name: '** TEST ROW **',
          email: 'test@example.com',
          phone: '555-123-4567',
          company: 'Test Company',
          submitted_at: new Date().toISOString(),
          event_name: 'Test Event',
          status: 'Test'
        };
      } else {
        action = 'append_coach_signup';
        testData = {
          coach_name: '** TEST ROW **',
          email: 'test@example.com',
          phone: '555-123-4567',
          college_name: 'Test College',
          division: 'Test Division',
          payment_status: 'Test',
          registered_at: new Date().toISOString()
        };
      }

      console.log('[IntegrationsTab] Sending test row to edge function:', {
        spreadsheetId,
        sheetName: sheetName || 'Sheet1',
        action,
        testData,
        columnMapping
      });

      const { data, error } = await invokeEdgeFunction('google-sheets-sync', {
        body: {
          spreadsheetId,
          sheetName: sheetName || 'Sheet1',
          action,
          data: testData,
          columnMapping
        }
      });

      console.log('[IntegrationsTab] Test row response:', { data, error });

      if (error) throw error;

      if (data?.success) {
        toast({
          title: 'Test Row Sent!',
          description: 'Check your Google Sheet for the test row.',
        });
      } else {
        throw new Error(data?.error || 'Failed to send test row');
      }
    } catch (error: any) {
      console.error('Test row error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to send test row',
        variant: 'destructive'
      });
    } finally {
      setSendingTestRow(null);
    }
  };

  const handleTestConnection = async (type: 'registration' | 'media' | 'coach') => {
    const spreadsheetId = type === 'registration' 
      ? formData.google_sheets_webhook_url 
      : type === 'media'
      ? (formData as any).media_credentials_sheets_id
      : (formData as any).coach_attendance_google_sheet_url;
    
    if (!spreadsheetId) return;
    
    if (type === 'registration') {
      setTestingConnection(true);
      setConnectionResult(null);
    } else if (type === 'media') {
      setTestingMediaConnection(true);
      setMediaConnectionResult(null);
    } else {
      setTestingCoachConnection(true);
      setCoachConnectionResult(null);
    }

    try {
      const { data, error } = await invokeEdgeFunction('google-sheets-sync', {
        body: {
          spreadsheetId,
          action: 'get_tabs' // This now returns tabs
        }
      });

      if (error) throw error;

      const result = {
        success: data?.success || false,
        message: data?.message || 'Connection failed',
        email: data?.serviceAccountEmail,
        tabs: data?.tabs || []
      };

      if (type === 'registration') {
        setConnectionResult(result);
      } else if (type === 'media') {
        setMediaConnectionResult(result);
      } else {
        setCoachConnectionResult(result);
      }

      if (result.success) {
        toast({
          title: 'Connection Successful',
          description: `Connected to ${result.message}. ${result.tabs?.length || 0} tabs found.`,
        });
      }
    } catch (error: any) {
      console.error('Connection test error:', error);
      const result = {
        success: false,
        message: error.message || 'Connection failed'
      };
      if (type === 'registration') {
        setConnectionResult(result);
      } else if (type === 'media') {
        setMediaConnectionResult(result);
      } else {
        setCoachConnectionResult(result);
      }
    } finally {
      if (type === 'registration') {
        setTestingConnection(false);
      } else if (type === 'media') {
        setTestingMediaConnection(false);
      } else {
        setTestingCoachConnection(false);
      }
    }
  };

  const copyEmail = (email: string) => {
    navigator.clipboard.writeText(email);
    toast({ title: 'Copied!', description: 'Service account email copied to clipboard' });
  };

  const renderIntegrationCard = (
    title: string,
    description: string,
    spreadsheetFieldKey: string,
    spreadsheetValue: string,
    onSpreadsheetUpdate: (val: string) => void,
    tabFieldKey: string,
    tabValue: string,
    onTabUpdate: (val: string) => void,
    testing: boolean,
    result: typeof connectionResult,
    onTestConnection: () => void,
    onOpenMapping: () => void,
    columns: typeof REGISTRATION_COLUMNS,
    integrationType: 'registration' | 'media' | 'coach'
  ) => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5" />
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor={spreadsheetFieldKey}>Google Spreadsheet ID</Label>
          <Input
            id={spreadsheetFieldKey}
            placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
            value={spreadsheetValue || ''}
            onChange={(e) => onSpreadsheetUpdate(e.target.value)}
          />
          <p className="text-sm text-muted-foreground">
            Find this in your spreadsheet URL: docs.google.com/spreadsheets/d/<strong>[SPREADSHEET_ID]</strong>/edit
          </p>
        </div>

        {/* Always show service account email */}
        <div className="p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md">
          <p className="text-sm font-medium mb-1">📋 Service Account Email (share your sheet with this):</p>
          <div className="flex items-center gap-2">
            <code className="text-xs bg-background px-2 py-1 rounded flex-1 overflow-x-auto font-mono">
              google-sheets@recruitlook.iam.gserviceaccount.com
            </code>
            <Button type="button" variant="ghost" size="sm" onClick={() => copyEmail('google-sheets@recruitlook.iam.gserviceaccount.com')}>
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Give this email "Editor" access to your Google Sheet</p>
        </div>

        {spreadsheetValue && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onTestConnection}
              disabled={testing}
            >
              {testing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Testing...
                </>
              ) : (
                'Test Connection'
              )}
            </Button>
            
            {result?.success && (
              <span className="text-sm text-green-600 flex items-center gap-1">
                <Check className="h-4 w-4" />
                {result.message}
              </span>
            )}
            {result && !result.success && (
              <span className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {result.message}
              </span>
            )}
          </div>
        )}

        {/* Tab Selection - shown after successful connection */}
        {result?.success && result.tabs && result.tabs.length > 0 && (
          <div className="space-y-2 pt-2 border-t">
            <Label htmlFor={tabFieldKey}>Select Tab (Sheet)</Label>
            <Select
              value={tabValue || ''}
              onValueChange={onTabUpdate}
            >
              <SelectTrigger id={tabFieldKey}>
                <SelectValue placeholder="Select a tab..." />
              </SelectTrigger>
              <SelectContent>
                {result.tabs.map((tab) => (
                  <SelectItem key={tab.id} value={tab.title}>
                    {tab.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Data will be synced to the selected tab within your spreadsheet.
            </p>
          </div>
        )}

        {/* Show selected tab even if connection not tested yet but tab is saved */}
        {tabValue && !result?.success && (
          <div className="text-sm text-muted-foreground p-2 bg-muted rounded">
            <strong>Selected Tab:</strong> {tabValue}
            <span className="text-xs ml-2">(Test connection to change)</span>
          </div>
        )}

        {result?.success && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onOpenMapping}
            >
              <Settings2 className="h-4 w-4 mr-2" />
              View Column Mapping
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => handleSendTestRow(integrationType)}
              disabled={sendingTestRow === integrationType}
            >
              {sendingTestRow === integrationType ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Test Row'
              )}
            </Button>
          </div>
        )}

        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Setup Instructions:</strong>
            <ol className="list-decimal ml-4 mt-2 space-y-1">
              <li>Create or open your Google Sheet</li>
              <li>Copy the Spreadsheet ID from the URL</li>
              <li>Paste it above and click "Test Connection"</li>
              <li>Copy the service account email shown</li>
              <li>Share your spreadsheet with that email (Editor access)</li>
              <li>Test connection again to verify and see available tabs</li>
              <li>Select the tab you want data synced to</li>
            </ol>
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Registration Google Sheets Integration */}
      {renderIntegrationCard(
        'Registration → Google Sheets',
        'Automatically sync team registration data to a Google Sheet.',
        'google_sheets_webhook_url',
        formData.google_sheets_webhook_url || '',
        (val) => updateFormData({ google_sheets_webhook_url: val }),
        'google_sheets_tab_name',
        (formData as any).google_sheets_tab_name || '',
        (val) => updateFormData({ google_sheets_tab_name: val } as any),
        testingConnection,
        connectionResult,
        () => handleTestConnection('registration'),
        () => handleOpenMappingDialog('registration'),
        REGISTRATION_COLUMNS,
        'registration'
      )}

      {/* Media Credentials Google Sheets Integration */}
      {renderIntegrationCard(
        'Media Credentials → Google Sheets',
        'Automatically sync media credential requests to a Google Sheet.',
        'media_credentials_sheets_id',
        (formData as any).media_credentials_sheets_id || '',
        (val) => updateFormData({ media_credentials_sheets_id: val } as any),
        'media_credentials_sheets_tab_name',
        (formData as any).media_credentials_sheets_tab_name || '',
        (val) => updateFormData({ media_credentials_sheets_tab_name: val } as any),
        testingMediaConnection,
        mediaConnectionResult,
        () => handleTestConnection('media'),
        () => handleOpenMappingDialog('media'),
        MEDIA_CREDENTIALS_COLUMNS,
        'media'
      )}

      {/* Coach Signups Google Sheets Integration */}
      {renderIntegrationCard(
        'Coach Signups → Google Sheets',
        'Automatically sync college coach registrations to a Google Sheet.',
        'coach_attendance_google_sheet_url',
        (formData as any).coach_attendance_google_sheet_url || '',
        (val) => updateFormData({ coach_attendance_google_sheet_url: val } as any),
        'coach_attendance_sheets_tab_name',
        (formData as any).coach_attendance_sheets_tab_name || '',
        (val) => updateFormData({ coach_attendance_sheets_tab_name: val } as any),
        testingCoachConnection,
        coachConnectionResult,
        () => handleTestConnection('coach'),
        () => handleOpenMappingDialog('coach'),
        COACH_SIGNUP_COLUMNS,
        'coach'
      )}

      {/* Registration Field Mapping Dialog */}
      <Dialog open={showMappingDialog} onOpenChange={setShowMappingDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Registration Field Mapping</DialogTitle>
            <DialogDescription>
              Map registration fields to your Google Sheet columns (from Row 1 headers).
            </DialogDescription>
          </DialogHeader>
          
          {loadingColumns ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              <span>Loading sheet columns...</span>
            </div>
          ) : sheetColumns.length === 0 ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No column headers found in Row 1. Make sure your Google Sheet has headers in the first row.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-3 py-4">
              {/* Duplicate column warning */}
              {(() => {
                const currentMapping = ((formData as any).google_sheets_column_mapping || {}) as Record<string, string>;
                const columnUsage: Record<string, string[]> = {};
                Object.entries(currentMapping).forEach(([field, col]) => {
                  if (col && col !== '') {
                    if (!columnUsage[col]) columnUsage[col] = [];
                    columnUsage[col].push(REGISTRATION_COLUMNS.find(c => c.key === field)?.label || field);
                  }
                });
                const duplicates = Object.entries(columnUsage).filter(([_, fields]) => fields.length > 1);
                if (duplicates.length > 0) {
                  return (
                    <Alert variant="destructive" className="mb-4">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>Warning: Duplicate column mappings detected!</strong> These fields will overwrite each other:
                        <ul className="mt-2 list-disc list-inside">
                          {duplicates.map(([col, fields]) => (
                            <li key={col}>Column {col}: {fields.join(', ')}</li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  );
                }
                return null;
              })()}
              <div className="grid grid-cols-3 gap-2 text-sm font-medium text-muted-foreground border-b pb-2">
                <span>Registration Field</span>
                <span>Sheet Column</span>
                <span>Field Key</span>
              </div>
              {REGISTRATION_COLUMNS.map((col) => {
                const currentMapping = ((formData as any).google_sheets_column_mapping || {}) as Record<string, string>;
                const currentColumn = currentMapping[col.key] || '';
                // Check if this column is used by multiple fields
                const columnUsage: Record<string, string[]> = {};
                Object.entries(currentMapping).forEach(([field, c]) => {
                  if (c && c !== '') {
                    if (!columnUsage[c]) columnUsage[c] = [];
                    columnUsage[c].push(field);
                  }
                });
                const isDuplicate = currentColumn && columnUsage[currentColumn]?.length > 1;
                return (
                  <div key={col.key} className={`grid grid-cols-3 gap-2 text-sm items-center ${isDuplicate ? 'bg-destructive/10 -mx-2 px-2 py-1 rounded' : ''}`}>
                    <span className="font-medium">{col.label}</span>
                    <Select
                      value={currentColumn || 'none'}
                      onValueChange={(val) => {
                        const newMapping = { ...currentMapping };
                        if (val === 'none') {
                          delete newMapping[col.key];
                        } else {
                          newMapping[col.key] = val;
                        }
                        updateFormData({ google_sheets_column_mapping: newMapping } as any);
                      }}
                    >
                      <SelectTrigger className={`h-8 text-xs ${isDuplicate ? 'border-destructive' : ''}`}>
                        <SelectValue placeholder="Select column..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {sheetColumns.map((sheetCol) => (
                          <SelectItem key={sheetCol.letter} value={sheetCol.letter}>
                            {sheetCol.letter}: {sheetCol.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-muted-foreground text-xs">{col.key}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Summary Row Configuration */}
          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium mb-3">Summary Row (Auto-updating Totals)</h4>
            <p className="text-sm text-muted-foreground mb-3">
              Configure cells to display running totals that update with each registration.
            </p>
            
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-sm font-medium text-muted-foreground border-b pb-2">
                <span>Summary Field</span>
                <span>Cell (e.g., A1)</span>
                <span>Description</span>
              </div>
              {SUMMARY_COLUMNS.map((col) => {
                const currentSummaryMapping = ((formData as any).google_sheets_summary_mapping || {}) as Record<string, string>;
                const currentCell = currentSummaryMapping[col.key] || '';
                return (
                  <div key={col.key} className="grid grid-cols-3 gap-2 text-sm items-center">
                    <span className="font-medium">{col.label}</span>
                    <Input
                      placeholder="e.g., A1"
                      value={currentCell}
                      onChange={(e) => {
                        const newMapping = { ...currentSummaryMapping, [col.key]: e.target.value.toUpperCase() };
                        updateFormData({ google_sheets_summary_mapping: newMapping } as any);
                      }}
                      className="h-8 text-xs uppercase"
                    />
                    <span className="text-muted-foreground text-xs">{col.key}</span>
                  </div>
                );
              })}
            </div>
            
            <div className="mt-4 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDownloadTemplate}
                disabled={downloadingHeaders || !eventId}
              >
                {downloadingHeaders ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Download Template CSV
                  </>
                )}
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                Downloads headers (Row 1) + formulas (Row 2). Paste into your Google Sheet, data syncs to Row 3+.
              </p>
            </div>
          </div>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Workflow:</strong> Download template → Paste rows 1-2 into Google Sheet → Configure column mapping → Formulas auto-calculate totals from synced data.
            </AlertDescription>
          </Alert>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMappingDialog(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media Credentials Field Mapping Dialog */}
      <Dialog open={showMediaMappingDialog} onOpenChange={setShowMediaMappingDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Media Credentials Field Mapping</DialogTitle>
            <DialogDescription>
              Configure how media credential requests map to your Google Sheet columns.
              The data will be appended in this order (A-J columns).
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            <div className="grid grid-cols-3 gap-2 text-sm font-medium text-muted-foreground border-b pb-2">
              <span>Column</span>
              <span>Field</span>
              <span>Description</span>
            </div>
            {MEDIA_CREDENTIALS_COLUMNS.map((col, idx) => (
              <div key={col.key} className="grid grid-cols-3 gap-2 text-sm items-center">
                <span className="font-mono text-muted-foreground">
                  {String.fromCharCode(65 + idx)}
                </span>
                <span className="font-medium">{col.label}</span>
                <span className="text-muted-foreground">{col.key}</span>
              </div>
            ))}
          </div>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Tip:</strong> Create column headers in your Google Sheet (Row 1) matching these field names.
              Data will be appended starting from Row 2.
            </AlertDescription>
          </Alert>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMediaMappingDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Coach Signups Field Mapping Dialog */}
      <Dialog open={showCoachMappingDialog} onOpenChange={setShowCoachMappingDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Coach Signups Field Mapping</DialogTitle>
            <DialogDescription>
              Configure how coach registration data maps to your Google Sheet columns.
              The data will be appended in this order (A-M columns).
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            <div className="grid grid-cols-3 gap-2 text-sm font-medium text-muted-foreground border-b pb-2">
              <span>Column</span>
              <span>Field</span>
              <span>Description</span>
            </div>
            {COACH_SIGNUP_COLUMNS.map((col, idx) => (
              <div key={col.key} className="grid grid-cols-3 gap-2 text-sm items-center">
                <span className="font-mono text-muted-foreground">
                  {String.fromCharCode(65 + idx)}
                </span>
                <span className="font-medium">{col.label}</span>
                <span className="text-muted-foreground">{col.key}</span>
              </div>
            ))}
          </div>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Tip:</strong> Create column headers in your Google Sheet (Row 1) matching these field names.
              Data will be appended starting from Row 2.
            </AlertDescription>
          </Alert>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCoachMappingDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}